import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const ChallengeContext = createContext(null);

export function ChallengeProvider({ children }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const prevMilestones = useRef(null);
  const prevBalance = useRef(null);

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get("/challenge");
      // milestone unlock notifications
      if (prevMilestones.current) {
        const fresh = res.data.unlocked_milestones.filter((m) => !prevMilestones.current.includes(m));
        fresh.forEach((m) =>
          toast.success("🏆 MILESTONE UNLOCKED", {
            description: `Gratulacje! Osiągnąłeś $${m.toLocaleString("en-US")}.`,
          })
        );
      }
      prevMilestones.current = res.data.unlocked_milestones;
      prevBalance.current = res.data.current_balance;
      setData(res.data);
      return res.data;
    } catch (e) {
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const saveSession = async (payload) => {
    await api.post("/challenge/session", payload);
    const label = payload.session ? `Tura ${payload.session}` : "Tura";
    const amt = payload.result_usd;
    if (payload.completed) {
      toast.success("🔥 TURA UKOŃCZONA", {
        description: `${label} zakończona wynikiem ${amt >= 0 ? "+" : ""}$${Number(amt || 0).toFixed(2)}.`,
      });
    }
    return refresh(true);
  };

  const resetDayOrSession = async (day, session) => {
    await api.post("/challenge/session/reset", { day, session });
    return refresh(true);
  };

  const saveReview = async (payload) => {
    await api.post("/challenge/review", payload);
    toast.success("Podsumowanie zapisane");
    return refresh(true);
  };

  const saveChecklist = async (day, items) => {
    await api.post("/challenge/checklist", { day, items });
    return refresh(true);
  };

  return (
    <ChallengeContext.Provider
      value={{ data, loading, refresh, saveSession, resetDayOrSession, saveReview, saveChecklist }}
    >
      {children}
    </ChallengeContext.Provider>
  );
}

export const useChallenge = () => useContext(ChallengeContext);
